import { slugify } from 'fym-common/src/utils/slugify';
import type { AccountSlugRefusal } from 'fym-shared/src/music24/accountSlug';
import { slugRefusalOf } from '../../smartLinks/profile/accountSlugRefusalText';

/** Why `onboarding.saveIdentity` refused the name and the address (design: onboarding a6). */
export type WelcomeSaveError = 'taken' | 'reserved' | 'invalid' | 'network';

export interface MappedSaveError {
  error: WelcomeSaveError;
  /** The free address the server offers with `M24_SLUG_TAKEN`. */
  suggestion?: string;
  /** Why the server refused the address (`data.reason`, QA brk-r2-4); absent on an older API. */
  refusal?: AccountSlugRefusal;
}

/**
 * The API puts the slug code in `error.data.errorType` and the free address in
 * `error.data.suggestion` (music24-api saveArtistIdentity.ts). Every other failure is "network".
 */
export function mapWelcomeSaveError(error: unknown): MappedSaveError {
  const data =
    typeof error === 'object' && error !== null
      ? (error as { data?: { errorType?: unknown; suggestion?: unknown; reason?: unknown } | null }).data
      : undefined;
  const refusal = slugRefusalOf({ reason: data?.reason }) ?? undefined;
  const errorType = data?.errorType;
  const suggestion = typeof data?.suggestion === 'string' && data.suggestion !== '' ? data.suggestion : undefined;
  switch (errorType) {
    case 'M24_SLUG_TAKEN':
      return { error: 'taken', suggestion };
    case 'M24_SLUG_RESERVED':
      return { error: 'reserved', refusal: 'reserved' };
    case 'M24_SLUG_INVALID':
      return refusal === undefined || refusal === 'reserved' ? { error: 'invalid' } : { error: 'invalid', refusal };
    default:
      return { error: 'network' };
  }
}

/**
 * The address field keeps only what the server `slugify` keeps: lower-case letters, digits,
 * `_` and dashes. A space becomes a dash.
 */
export function cleanAddressInput(value: string): string {
  return slugify(value.toLowerCase().replace(/\s+/g, '-'));
}
